import type { TokenAnalysisResult, FrequentlyQuotedTweet } from '../api/bipApi';
import { FALLBACK_AVATAR_LOGO } from '../constants';
import SmartMoneyTable from './SmartMoneyTable';
import AdminModSection from './AdminModSection';
import MarkdownParagraph from './MarkdownParagraph';
import TokenInfoSection from './TokenInfoSection';

interface ResultDisplayProps {
  result: TokenAnalysisResult;
}

/** US local time, US-style (e.g. "Feb 24, 2026, 8:43 AM") */
function formatTweetDateUS(iso?: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

function TweetCard({ tweet, index }: { tweet: FrequentlyQuotedTweet; index: number }) {
  const stats: string[] = [];
  if (tweet.viewCount != null && tweet.viewCount > 0) stats.push(`👁️ ${tweet.viewCount.toLocaleString()}`);
  if (tweet.likeCount != null) stats.push(`❤️ ${tweet.likeCount.toLocaleString()}`);
  if (tweet.retweetCount != null) stats.push(`🔄 ${tweet.retweetCount.toLocaleString()}`);
  if (tweet.replyCount != null) stats.push(`💬 ${tweet.replyCount.toLocaleString()}`);
  const hasAuthor = tweet.authorName || tweet.authorHandle;
  const dateStr = formatTweetDateUS(tweet.createdAt);

  const content = (
    <>
      {hasAuthor && (
        <div className="frequently-quoted-author">
          {tweet.authorPfp ? (
            <img
              src={tweet.authorPfp}
              alt=""
              className="frequently-quoted-author-pfp"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = FALLBACK_AVATAR_LOGO;
              }}
            />
          ) : (
            <div className="frequently-quoted-author-pfp frequently-quoted-author-pfp--placeholder" />
          )}
          <div className="frequently-quoted-author-info">
            <span className="frequently-quoted-author-name">
                {tweet.authorName ? tweet.authorName : <span className="value-empty">—</span>}
              </span>
            <span className="frequently-quoted-author-handle">
              {tweet.authorHandle ? `@${tweet.authorHandle.replace(/^@/, '')}` : ''}
              {tweet.authorFollowers != null && tweet.authorFollowers > 0 && (
                <> · {tweet.authorFollowers.toLocaleString()} followers</>
              )}
            </span>
          </div>
        </div>
      )}
      <p className="frequently-quoted-text">
        {tweet.text || (tweet.url ? 'View tweet' : <span className="value-empty">—</span>)}
      </p>
      {(stats.length > 0 || dateStr) && (
        <div className="frequently-quoted-meta">
          {stats.length > 0 && (
            <div className="frequently-quoted-stats-row">{stats.join(' · ')}</div>
          )}
          {dateStr && (
            <div className="frequently-quoted-date-row">{dateStr}</div>
          )}
        </div>
      )}
    </>
  );

  const className = `frequently-quoted-item${tweet.url ? ' frequently-quoted-item--link' : ''}`;

  if (tweet.url) {
    return (
      <a
        href={tweet.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={{ animationDelay: `${0.7 + 0.1 + index * 0.05}s` }}
      >
        {content}
      </a>
    );
  }

  return (
    <div
      className={className}
      style={{ animationDelay: `${0.7 + 0.1 + index * 0.05}s` }}
    >
      {content}
    </div>
  );
}

export default function ResultDisplay({ result }: ResultDisplayProps) {
  return (
    <div className="result-container">
      <div className="result-content">
        {result.token && (
          <TokenInfoSection token={result.token} animationDelay="0.05s" />
        )}

        {result.content?.text && (
          <section className="result-section" id="section-token-overview">
            <div className="section-title-block">
              <h2 className="section-title fade-in-line" style={{ animationDelay: '0.1s', marginBottom: "2.0rem" }}>
                Token Overview
              </h2>
            </div>
            <MarkdownParagraph
              content={result.content.text}
              enableLineAnimation={true}
              animationDelay={0.15}
            />
          </section>
        )}

        {result.smartMoney && (
          <SmartMoneyTable
            text={result.smartMoney.text}
            table={result.smartMoney.table}
            animationDelay="0.3s"
          />
        )}

        {result.adminMod && (
          <AdminModSection
            text={result.adminMod.text}
            table={result.adminMod.table}
            animationDelay="0.5s"
          />
        )}

        {(result.tweetSections?.mostViewed?.length || result.tweetSections?.highFollower?.length || result.tweetSections?.kol?.length) ? (
          <section className="result-section fade-in" id="section-tweets" style={{ animationDelay: '0.7s' }}>
            <div className="section-title-block">
              <h2 className="section-title">Tweets</h2>
            </div>
            <div className="tweets-columns">
              <div className="tweets-column">
                <h3 className="tweets-column-title">Most viewed</h3>
                <div className="tweets-list" style={{ maxHeight: 1000, overflowY: 'auto' }}>
                  {(result.tweetSections.mostViewed ?? []).map((tweet, index) => (
                    <TweetCard key={`most-${tweet.id ?? index}-${index}`} tweet={tweet} index={index} />
                  ))}
                </div>
              </div>
              <div className="tweets-column">
                <h3 className="tweets-column-title">High Follower</h3>
                <div className="tweets-list" style={{ maxHeight: 1000, overflowY: 'auto' }}>
                  {(result.tweetSections.highFollower ?? []).map((tweet, index) => (
                    <TweetCard key={`hf-${tweet.id ?? index}-${index}`} tweet={tweet} index={index} />
                  ))}
                </div>
              </div>
              <div className="tweets-column">
                <h3 className="tweets-column-title">KoL</h3>
                <div className="tweets-list" style={{ maxHeight: 1000, overflowY: 'auto' }}>
                  {(result.tweetSections.kol ?? []).map((tweet, index) => (
                    <TweetCard key={`kol-${tweet.id ?? index}-${index}`} tweet={tweet} index={index} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
